import { useState, type ReactNode } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Search, Sparkles, Store } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, isStaff, LISTING_SORTS, type Category, type Listing, type ListingKind, type ListingSort } from "@cd/core";
import { api } from "../../lib/api";
import { cn, useDebouncedValue } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input, Select, Skeleton } from "../../components/ui/primitives";
import { ListingCard } from "./ListingCard";
import { MyListings } from "./MyListings";

const SORT_LABELS: Record<ListingSort, string> = { popular: "Popular", new: "New", liked: "Most liked" };
const KINDS: { id?: ListingKind; label: string }[] = [{ label: "All" }, { id: "dashboard", label: "Dashboards" }, { id: "widget", label: "Widgets" }];

const TAB_LABELS = { browse: "Browse", mine: "My listings", all: "All listings" } as const;

/** Browse and search what people have published; or see your own listings (super users: everyone's). */
export function MarketplacePage() {
  const [tab, setTab] = useState<keyof typeof TAB_LABELS>("browse");
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  // Never while viewing as someone (viewAsTier is unset then).
  const tabs = me.data?.viewAsTier === "super" ? (["browse", "mine", "all"] as const) : (["browse", "mine"] as const);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<ListingKind>();
  const [category, setCategory] = useState<Category>();
  const [tag, setTag] = useState<string>();
  const [sort, setSort] = useState<ListingSort>("popular");
  const debounced = useDebouncedValue(q.trim(), 250);

  const facets = useQuery({ queryKey: ["marketplace-facets"], queryFn: api.marketplaceFacets, enabled: tab === "browse" });
  const search = { q: debounced || undefined, kind, category, tag, sort };
  const results = useInfiniteQuery({
    queryKey: ["marketplace", search],
    queryFn: ({ pageParam }) => api.marketplace({ ...search, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    enabled: tab === "browse",
  });
  const listings = results.data?.pages.flatMap((p) => p.results) ?? [];
  // The front page: featured first, then everything grouped by category.
  const isDefaultView = !search.q && !category && !tag && sort === "popular";

  return (
    <div className="mx-auto max-w-[1400px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 flex flex-wrap items-center gap-3 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><Store className="size-4 text-subtle" /> Marketplace</h1>
        <div className="flex rounded-lg border border-border-strong p-0.5">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn("rounded-md px-2.5 py-1 text-xs font-medium", tab === t ? "bg-surface-2 text-foreground" : "text-muted hover:text-foreground")}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
        {tab === "browse" && (
          <>
            <div className="relative min-w-48 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dashboards and widgets" className="pl-8" aria-label="Search the marketplace" />
            </div>
            <div className="flex gap-1">
              {KINDS.map((k) => (
                <Button key={k.label} size="sm" variant={kind === k.id ? "primary" : "ghost"} onClick={() => setKind(k.id)}>{k.label}</Button>
              ))}
            </div>
            <Select value={sort} onChange={(e) => setSort(e.target.value as ListingSort)} aria-label="Sort by">
              {LISTING_SORTS.map((s) => <option key={s} value={s}>{SORT_LABELS[s]}</option>)}
            </Select>
          </>
        )}
      </header>

      {tab !== "browse" ? (
        <MyListings all={tab === "all"} />
      ) : (
        <div className="flex gap-6">
          <aside className="hidden w-48 shrink-0 md:block">
            <nav className="sticky top-20 space-y-0.5">
              <RailItem active={!category} onClick={() => setCategory(undefined)} label="All categories" />
              {facets.data?.categories.map((c) => (
                <RailItem key={c.id} active={category === c.id} onClick={() => setCategory(c.id)} label={c.label} count={c.count} />
              ))}
              {!!facets.data?.tags.length && (
                <div className="pt-4">
                  <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">Popular tags</div>
                  <div className="flex flex-wrap gap-1 px-2">
                    {facets.data.tags.map((t) => (
                      <button
                        key={t.tag}
                        onClick={() => setTag(tag === t.tag ? undefined : t.tag)}
                        className={cn("rounded-md px-1.5 py-0.5 text-[11px]", tag === t.tag ? "bg-accent-soft text-accent" : "bg-surface-2 text-muted hover:text-foreground")}
                      >
                        #{t.tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </nav>
          </aside>

          <div className="min-w-0 flex-1 space-y-8">
            <div className="md:hidden">
              <Select value={category ?? ""} onChange={(e) => setCategory((e.target.value || undefined) as Category | undefined)} aria-label="Category">
                <option value="">All categories</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </Select>
            </div>
            {tag && (
              <p className="text-sm text-muted">
                Tagged <span className="font-medium text-foreground">#{tag}</span>{" "}
                <button className="text-accent hover:underline" onClick={() => setTag(undefined)}>Clear</button>
              </p>
            )}
            {results.isLoading ? (
              <Grid>{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-40" />)}</Grid>
            ) : results.error ? (
              <p className="text-sm text-bad">{results.error.message}</p>
            ) : !listings.length ? (
              <EmptyMarket filtered={!isDefaultView || Boolean(kind)} />
            ) : isDefaultView ? (
              <FrontPage listings={listings} onTag={setTag} />
            ) : (
              <Grid>{listings.map((l) => <ListingCard key={l.id} listing={l} onTag={setTag} />)}</Grid>
            )}
            {results.hasNextPage && (
              <div className="flex justify-center">
                <Button disabled={results.isFetchingNextPage} onClick={() => void results.fetchNextPage()}>Load more</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FrontPage({ listings, onTag }: { listings: Listing[]; onTag: (t: string) => void }) {
  const featured = listings.filter((l) => l.featured);
  return (
    <>
      {featured.length > 0 && (
        <Section title="Featured" icon={<Sparkles className="size-3.5 text-accent" />}>
          <Grid>{featured.map((l) => <ListingCard key={l.id} listing={l} onTag={onTag} />)}</Grid>
        </Section>
      )}
      {CATEGORIES.map((c) => {
        const inCategory = listings.filter((l) => l.category === c && !l.featured);
        if (!inCategory.length) return null;
        return (
          <Section key={c} title={CATEGORY_LABELS[c]}>
            <Grid>{inCategory.map((l) => <ListingCard key={l.id} listing={l} onTag={onTag} />)}</Grid>
          </Section>
        );
      })}
    </>
  );
}

const Grid = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">{children}</div>
);

function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold">{icon}{title}</h2>
      {children}
    </section>
  );
}

function RailItem({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-sm",
        active ? "bg-surface-2 font-medium text-foreground" : "text-muted hover:bg-surface-2 hover:text-foreground",
      )}
    >
      <span className="truncate">{label}</span>
      {count !== undefined && <span className="text-xs tabular-nums text-subtle">{count}</span>}
    </button>
  );
}

function EmptyMarket({ filtered }: { filtered: boolean }) {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const canPublish = me.data ? isStaff(me.data) : false;
  return (
    <div className="mt-16 flex flex-col items-center text-center">
      <Store className="size-6 text-subtle" />
      <h2 className="mt-3 font-semibold">{filtered ? "Nothing matches" : "Nothing published yet"}</h2>
      <p className="mt-1 max-w-sm text-sm text-muted">
        {filtered
          ? "Try a different search or category."
          : canPublish ? "Publish a dashboard or widget from its menu to share it with everyone on GoTab." : "Check back soon for dashboards and widgets from GoTab."}
      </p>
    </div>
  );
}
